import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { listVideos } from "@/lib/api";

const SearchBar = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      listVideos({ q, limit: 8 })
        .then(setResults)
        .catch((err) => { console.error("Search failed:", err); setResults([]); })
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submit = (id) => {
    setOpen(false);
    setQ("");
    navigate(`/watch/${id}`);
  };

  return (
    <div ref={boxRef} className="relative" data-testid="search-wrapper">
      <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-[var(--bg-elevated)] border border-[var(--border)] focus-within:border-[var(--accent)] transition-colors w-full sm:w-72">
        <Search className="w-4 h-4 text-[var(--text-muted)] shrink-0" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Procurar vídeos…"
          className="bg-transparent outline-none text-sm flex-1 placeholder:text-[var(--text-muted)] text-white"
          data-testid="search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setResults([]); }} data-testid="search-clear">
            <X className="w-4 h-4 text-[var(--text-muted)] hover:text-white" />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div
          className="absolute top-[110%] right-0 left-0 sm:left-auto sm:w-[420px] z-40 rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-2xl overflow-hidden"
          data-testid="search-results"
        >
          {loading ? (
            <div className="p-4 text-sm text-[var(--text-dim)]">A pesquisar…</div>
          ) : results.length === 0 ? (
            <div className="p-4 text-sm text-[var(--text-dim)]">Nenhum resultado para &ldquo;{q}&rdquo;</div>
          ) : (
            <ul className="max-h-[420px] overflow-auto">
              {results.map((v) => (
                <li key={v.id}>
                  <button
                    onClick={() => submit(v.id)}
                    className="w-full flex gap-3 p-3 hover:bg-[var(--bg-hover)] transition-colors text-left"
                    data-testid={`search-result-${v.id}`}
                  >
                    <img src={v.thumbnail} alt="" className="w-20 h-12 object-cover rounded-md shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-white clamp-2 leading-snug">{v.title}</div>
                      <div className="text-[11px] text-[var(--text-muted)] mt-1">
                        {v.views} visualizações {v.is_short ? "· Short" : ""}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
